import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";

type Rect = { left: number; top: number; width: number; height: number };
type Flight = {
  id: string;
  from: Rect;
  to: Rect;
  label: string;
};

type Api = {
  /** Register a field's current bounding rect under the given field_key. */
  registerField: (field_key: string, el: HTMLElement | null) => void;
  /** Trigger the snap animation from the given source rect to the matching field. */
  fly: (field_key: string, sourceRect: Rect, label: string) => Promise<void>;
};

const Ctx = createContext<Api | null>(null);

export function useSnapToField(): Api {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSnapToField must be used inside <SnapToFieldLayer>");
  return ctx;
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function SnapToFieldLayer({ children }: { children: ReactNode }): JSX.Element {
  const fields = useRef(new Map<string, HTMLElement>());
  const [flight, setFlight] = useState<Flight | null>(null);

  const registerField = useCallback((field_key: string, el: HTMLElement | null) => {
    if (el) fields.current.set(field_key, el);
    else fields.current.delete(field_key);
  }, []);

  const fly = useCallback(async (field_key: string, sourceRect: Rect, label: string) => {
    const target = fields.current.get(field_key);
    if (!target) return;
    const reduced = prefersReducedMotion();
    const tRect = target.getBoundingClientRect();
    const f: Flight = {
      id: `${field_key}-${Date.now()}`,
      from: sourceRect,
      to: { left: tRect.left, top: tRect.top, width: tRect.width, height: tRect.height },
      label,
    };

    if (reduced) {
      // Reduced-motion fallback: skip travel, brief pulse only.
      return new Promise<void>((resolve) => {
        target.classList.add("ring-accent");
        window.setTimeout(() => {
          target.classList.remove("ring-accent");
          resolve();
        }, 200);
      });
    }

    setFlight(f);
    // Pulse the target after the flight completes (~340ms).
    return new Promise<void>((resolve) => {
      window.setTimeout(() => {
        target.classList.add("ring-accent");
        window.setTimeout(() => {
          target.classList.remove("ring-accent");
          setFlight(null);
          resolve();
        }, 300);
      }, 320);
    });
  }, []);

  return (
    <Ctx.Provider value={{ registerField, fly }}>
      {children}
      <AnimatePresence>
        {flight ? (
          <motion.div
            key={flight.id}
            initial={{
              left: flight.from.left,
              top: flight.from.top,
              width: flight.from.width,
              height: flight.from.height,
              opacity: 1,
              scale: 1,
            }}
            animate={{
              left: flight.to.left,
              top: flight.to.top,
              width: flight.to.width,
              height: flight.to.height,
              opacity: 0,
              scale: 0.55,
            }}
            transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-none fixed z-[60] rounded border-2 border-accent bg-accent/15"
            aria-hidden
          >
            <span className="sr-only">Suggerimento accettato: {flight.label}</span>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Ctx.Provider>
  );
}

/**
 * Hook component for form fields to register their rect.
 * Usage: <RegisterField field_key="ape">{(ref) => <input ref={ref} ... />}</RegisterField>
 */
export function RegisterField({
  field_key,
  children,
}: {
  field_key: string;
  children: (ref: (el: HTMLElement | null) => void) => ReactNode;
}): JSX.Element {
  const { registerField } = useSnapToField();
  return <>{children((el) => registerField(field_key, el))}</>;
}
